/* @flow */
import PropTypes from 'prop-types';
import * as React from 'react';
import type { Element } from 'react';
import Box from 'components/Box';
import Field from 'components/Field';
import Button from 'components/ButtonDeprecated';
import { debounce } from 'lodash';
import BoxBody from 'components/BoxBody';
import BoxHeader from 'components/BoxHeader';
import BoxFooter from 'components/BoxFooter';
import CenteredContainer from 'components/CenteredContainer';
import Link from 'components/Link';
import PrependedTextInput from 'components/PrependedTextInput';
import SettingsActions from 'actions/SettingsActions';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import LinkConstants from 'constants/LinkConstants';
import { SEARCH_NETWORK_LIMIT_MS } from 'chat/constants/ChatConstants';

import './styles/JoinSearchPage.scss';

type State = {
  group: ?Group,
  error: ?string,
  disabled: boolean,
  showLegalFooter: boolean,
  searching: boolean,
  value: string,
};

export default class JoinSearchPage extends React.Component<{}, State> {
  state: State = {
    group: null,
    error: null,
    disabled: true,
    showLegalFooter: false,
    searching: false,
    value: '',
  };
  // eslint-disable-next-line @remind/remind/legacy-context
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  componentWillMount() {
    const notFoundCode = this.context.router.location.query.notFound || '';
    if (notFoundCode) {
      this.onNotFound();
      this.setState({ value: notFoundCode });
    }
  }

  componentDidMount() {
    const onGeoReceived = geo => {
      if (geo.country_code === 'US') {
        this.setState({ showLegalFooter: true });
      }
    };

    SettingsActions.getGeo().then(onGeoReceived, () => {});
  }

  // eslint-disable-next-line @remind/remind/no-on-functions
  onInputChange = (e: SyntheticEvent<>) => {
    this.setState({ group: null, error: null, disabled: true });

    // TODO - cannot use regex from settings because JS does not support \A
    let code = '';
    if (e.target instanceof window.HTMLInputElement) {
      code = e.target.value.replace(/[^a-z0-9-]/gi, '');
      this.setState({ value: code });
    }

    if (code.length < 3) {
      return;
    }

    this.setState({ searching: true });
    this.searchGroupByCode(this, code);
  };
  // eslint-disable-next-line @remind/remind/no-on-functions
  onSubmit = () => {
    if (this.state.group) {
      this.context.router.push(`/join/${this.state.group.getCodeOrError()}`);
    }
  };
  // eslint-disable-next-line @remind/remind/no-on-functions
  onNotFound = () => {
    const error = `
      Uh oh! We couldn’t find a class matching this code.
      Every class has a unique code. Please contact your
      teacher to double check instructions for your class.`;
    this.setState({ group: null, disabled: true, error, searching: false });
  };
  searchGroupByCode = debounce(async (component: Element, code: string) => {
    const group = await GroupActions.searchGroupByCode(code);
    if (group) {
      component.setState({ group, disabled: false, searching: false });
    } else {
      this.onNotFound();
    }
  }, SEARCH_NETWORK_LIMIT_MS);
  renderLegalFooter() {
    if (!this.state.showLegalFooter) {
      return null;
    }
    return (
      <BoxFooter className="JoinSearchPage-Footer">
        <p>
          Remind uses the short code, 81010, to send alerts from teachers to students and parents.
          You may opt-in by using this Join page or by texting the class code directly to 81010. You
          may opt-out at any time by texting STOP to 81010.
        </p>

        <p>Message frequency may vary by class. Standard message & data rates may apply.</p>

        <p>
          Text HELP to 81010 for additional information, or{' '}
          <Link to={LinkConstants.CONTACT_FORM}>contact us</Link>.
        </p>
      </BoxFooter>
    );
  }
  render() {
    return (
      <CenteredContainer size="small">
        <Box className="JoinSearchPage">
          <BoxHeader title="Find your class" />
          <BoxBody>
            <Field error={this.state.error} loading={this.state.searching}>
              <PrependedTextInput
                data-test-selector="join_search_input"
                value={this.state.value}
                onChange={this.onInputChange}
                placeholder="Class code"
                autofocus
                prepend="@"
                type="text"
              />
            </Field>

            <Button
              onClick={this.onSubmit}
              data-test-selector="join_search_submit"
              disabled={this.state.disabled}>
              {this.state.group ? `Join ${this.state.group.getPossessiveName()}` : 'Enter'}
            </Button>
          </BoxBody>
          {this.renderLegalFooter()}
        </Box>
      </CenteredContainer>
    );
  }
}
